/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {AUTHENTICATION_PROVIDER} from '../../../services/authentication/authentication-provider.token';
import {ModalService} from '@siemens/ix-angular';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {LogoService} from '../../shared/services/logo/logo.service';

type LoginErrorType = 'noUser' | 'unknown' | 'forbidden' | 'inactivityLogout';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  protected readonly auth = inject(AUTHENTICATION_PROVIDER);
  protected readonly modalService = inject(ModalService);
  protected readonly route = inject(ActivatedRoute);
  private readonly logoService = inject(LogoService);
  protected readonly logo = this.logoService.resolve();
  protected readonly hideFooter = this.logoService.resolveHideFooter();

  @ViewChild('loginErrorModal', {read: TemplateRef})
  loginErrorModalRef!: TemplateRef<never>;

  private routeSubscription?: Subscription;
  errorType: LoginErrorType = 'unknown';

  ngOnInit() {
    this.routeSubscription = this.route.fragment.subscribe((fragment) => {
      this.onFragmentChange(fragment);
    });
  }

  async onFragmentChange(fragment: string | null) {
    await new Promise((resolve) => setTimeout(resolve, 0));

    if (fragment === 'errorNoUser') {
      this.errorType = 'noUser';
      this.openModal();
    } else if (fragment === 'unknownError') {
      this.errorType = 'unknown';
      this.openModal();
    } else if (fragment === 'errorForbidden') {
      this.errorType = 'forbidden';
      this.openModal();
    } else if (fragment === 'inactivityLogout') {
      this.errorType = 'inactivityLogout';
      this.openModal();
    } else if (fragment === 'register') {
      await this.onRegisterLinkClick();
    } else {
      return;
    }
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
  }

  async onLoginClick(): Promise<void> {
    await this.auth.login();
  }

  async onRegisterLinkClick(): Promise<void> {
    await this.auth.login({registration: true});
  }

  async openModal() {
    await this.modalService.open({
      content: this.loginErrorModalRef,
      centered: true,
    });
  }
}
